import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { isEmpty } from 'lodash-es';

import EmptyBox from './EmptyBox';
import TotalCount from './TotalCount';
import { skeletonTable } from '../../../Skeleton';
import SimpleDropdown from '../../../Dropdowns/SimpleDropdown';
import SortableTable from '../../../SortableTable/SortableTable';

import { format } from '../../../../utils/date';

const emptyArray = [];
const tableColumns = [
  { dataField: 'name', text: 'SDK' },
  { dataField: 'latest_install', text: 'Latest Install', className: 'text-right' },
  { dataField: 'total_publisher_apps_count', text: '# Apps' },
];

const skeletonRows = skeletonTable({
  columns: ['name', 'latest_install', 'total_publisher_apps_count'],
  height: 24,
});

const getSdkOptions = (sdksData) => {
  if (isEmpty(sdksData)) {
    return [{ label: 'Top SDKs', value: 'Top SDKs' }];
  }

  return Object.keys(sdksData).map(funcName => ({
    label: `Top ${SDK_FUNCTIONS[funcName]} SDKs`,
    value: funcName,
  }));
};

const PubSdks = ({
  settings,
  publisher,
  sdksData,
}) => {
  const sdkOptions = useMemo(() => getSdkOptions(sdksData), [sdksData]);
  const [currentFunction, setCurrentFunction] = useState(sdkOptions[0].value);
  const showData = !settings.error;
  const activeSdkData = !isEmpty(sdksData) && !isEmpty(currentFunction)
    ? sdksData[currentFunction] : emptyArray;
  const showEmptyState = !settings.pending && isEmpty(activeSdkData) && !settings.error;

  // eslint-disable-next-line
  const rows = useMemo(() => isEmpty(activeSdkData)
    ? emptyArray
    : activeSdkData.slice(0, 3).map(item => ({
      name: <div className="text-truncate">{item.name}</div>,
      latest_install: format(item.latest_install, 'MMM DD, YYYY'),
      total_publisher_apps_count: (
        <span>{item.total_publisher_apps_count}/{publisher.total_apps}</span>
      ),
    })), [activeSdkData, publisher.total_apps]);

  const handleFuncChange = useCallback((funcValue) => {
    setCurrentFunction(funcValue);
  }, []);

  useEffect(() => {
    setCurrentFunction(sdkOptions[0].value);
  }, [sdkOptions]);

  return (
    <div className="prospects-sidebar__sdks prospects-sidebar__box">
      {!settings.pending && (
        <SimpleDropdown
          selected={currentFunction}
          options={sdkOptions}
          frameless
          menuWidth={270}
          unselectedStateText="Top SDKs"
          onChange={handleFuncChange}
        />
      )}
      {showData && (
        <Fragment>
          <SortableTable
            tableClassName={cx('publisher-table', settings.pending && 'publisher-table__pending')}
            theadClassName="publisher-table-thead"
            tbodyClassName="publisher-table-tbody"
            rows={settings.pending ? skeletonRows : rows}
            columns={tableColumns}
            emptyStateText="No data to display"
            showMoreCellColSpan={tableColumns.length}
            initialRowsLimit={skeletonRows.length}
            hasShowMoreCell={false}
          />
          <TotalCount
            publisher={publisher}
            totalCount={!isEmpty(activeSdkData) ? activeSdkData.length : 0}
          />
        </Fragment>
      )}
      {showEmptyState && <EmptyBox text="No SDKs detected" />}
    </div>
  );
};

PubSdks.propTypes = {
  settings: T.object.isRequired,
  publisher: T.object.isRequired,
  sdksData: T.object,
};

export default PubSdks;
